import React, { useState, useEffect, useRef } from 'react';
import { Switch, Field, Label } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import {
  useGenerateDataProductSpecificationMutation,
  useLazyGetMetadataFileContentQuery,
  useGetSnowflakeAccountsQuery,
  useValidateSnowflakeAccountMutation,
} from 'app/createApi';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import {
  selectCreateNewProject,
  selectSelectedGroup,
  selectSelectedProject,
  selectProjectsCreatedInCurrentSession,
  selectNewProjectName,
  setProjectPath,
  setProjectsCreatedInCurrentSession,
  setBranch,
} from 'app/sharedSlice';
import {
  selectSnowflakeAccount,
  selectSnowflakeAccountConnected,
  setSnowflakeAccount,
  setSnowflakeAccountConnected,
  setSnowflakeAccountLabel,
} from 'app/snowflakeSlice';
import {
  setTablesFromStoredMetadata,
  setSkipSnowflakeConnectionStep,
  selectName,
  setName,
  selectDescription,
  setDescription,
  selectVersion,
  setVersion,
  selectJiraTicketNo,
  setJiraTicketNo,
  selectDataProductManager,
  setDataProductManager,
  selectVersionDescription,
  setVersionDescription,
  selectSchemaDescription,
  setSchemaDescription,
  selectDataProductCategory,
  setDataProductCategory,
  selectDataProductType,
  setDataProductType,
  selectContainsPatientData,
  setContainsPatientData,
  selectContainsPersonalData,
  setContainsPersonalData,
  selectIsGxpValidated,
  setIsGxpValidated,
  selectIsSoxControlled,
  setIsSoxControlled,
  selectSecurityClassification,
  setSecurityClassification,
  selectPurpose,
  setPurpose,
  selectSpecification1,
  setSpecification1,
  selectSourceSystem,
  setSourceSystem,
  setProcessingFrequency,
  selectProcessingFrequency,
  setGradCode,
  selectGradCode,
  setLineOfBusinessOwningDP,
  selectLineOfBusinessOwningDP,
  setDataDomain,
  selectDataDomain,
  setRelationships,
  selectRelationships,
  selectDerivedBusinessUseCase,
  selectDerivedProgram,
  setUrl,
  selectUrl,
  setBusinessTerm,
  selectBusinessTerm,
  setBusinessTermDefinition,
  selectBusinessTermDefinition,
  selectIsPowerUserMode,
  setIsPowerUserMode,
  setReviewedByInformationArchitect,
  setModels,
} from '../reducers/astraZenecaSlice';
import { setDataProductName, clearSelectedObjects } from 'features/data-product-builder/reducers/builderSlice';
import StepWrapper from 'components/StepWrapper';
import GroupAndProjectSelector from 'components/GroupAndProjectSelector';
import LoadingAndErrorSection from 'components/LoadingAndErrorSection';
import DividerWithText from 'components/DividerWithText';
import DropdownSingleselect from 'components/DropdownSingleselect';
import InformationTooltip from 'components/InformationTooltip';
import UseCaseMultiSelect from '../components/UseCaseMultiSelect';
import { PROGRAM_OPTIONS } from '../components/ProgramMultiSelect';
import { classNames } from 'utils/styleUtils';
import { getCamelCase, isProjectNameValid } from 'utils/formatUtils';
import { USE_CASE_PROGRAM_MAPPING, DEFAULT_PROGRAM } from '../constants/useCaseProgramMapping';

export interface DataProductDefinitionStepProps {
  onBack: () => void;
  onContinue: () => void;
}

const defaultErrorMessage =
  'Oops! Failed to create the project. Please try again or contact support@dataops.live if the issue persists.';

// Function to get Data Domain options based on Line of Business
const getDataDomainOptions = (lineOfBusiness: string): string[] => {
  if (lineOfBusiness === '') {
    return []; // Return empty list if no Line of Business is selected
  } else if (lineOfBusiness === 'Operations') {
    return [
      'Develop',
      'CM&S',
      'GSC&S',
      'Make',
      'Master',
      'Quality',
      'GERE',
      'Procurement',
      'ESM',
      'Sustainability',
      'DDP',
    ];
  } else if (lineOfBusiness === 'Enabling') {
    return ['SO', 'HR'];
  } else {
    return ['TBD'];
  }
};

export default function DataProductDefinitionStep(props: DataProductDefinitionStepProps): JSX.Element {
  const dispatch = useAppDispatch();
  const createNewProject = useAppSelector(selectCreateNewProject);
  const selectedGroup = useAppSelector(selectSelectedGroup);
  const selectedProject = useAppSelector(selectSelectedProject);
  const projectsCreatedInCurrentSession = useAppSelector(selectProjectsCreatedInCurrentSession);
  const newProjectName = useAppSelector(selectNewProjectName);
  const snowflakeAccount = useAppSelector(selectSnowflakeAccount);
  const snowflakeAccountConnected = useAppSelector(selectSnowflakeAccountConnected);

  const name = useAppSelector(selectName);
  const description = useAppSelector(selectDescription);
  const version = useAppSelector(selectVersion);
  const jiraTicket = useAppSelector(selectJiraTicketNo);
  const dataProductManager = useAppSelector(selectDataProductManager);
  const versionDescription = useAppSelector(selectVersionDescription);
  const schemaDescription = useAppSelector(selectSchemaDescription);
  const dataProductCategory = useAppSelector(selectDataProductCategory);
  const dataProductType = useAppSelector(selectDataProductType);
  const containsPatientData = useAppSelector(selectContainsPatientData);
  const containsPersonalData = useAppSelector(selectContainsPersonalData);
  const isGxpValidated = useAppSelector(selectIsGxpValidated);
  const isSoxControlled = useAppSelector(selectIsSoxControlled);
  const purpose = useAppSelector(selectPurpose);
  const specification = useAppSelector(selectSpecification1);
  const sourceSystem = useAppSelector(selectSourceSystem);

  const processingFrequency = useAppSelector(selectProcessingFrequency);
  const gradCode = useAppSelector(selectGradCode);
  const lineOfBusinessOwningDP = useAppSelector(selectLineOfBusinessOwningDP);
  const dataDomain = useAppSelector(selectDataDomain);
  const relationships = useAppSelector(selectRelationships);
  const businessUseCase = useAppSelector(selectDerivedBusinessUseCase);
  const program = useAppSelector(selectDerivedProgram);
  const url = useAppSelector(selectUrl);
  const businessTerm = useAppSelector(selectBusinessTerm);
  const businessTermDefinition = useAppSelector(selectBusinessTermDefinition);
  const securityClassification = useAppSelector(selectSecurityClassification);

  const isPowerUserMode = useAppSelector(selectIsPowerUserMode);

  // Task 2.1 — local pair-builder state: currently selected use case (before adding)
  // and the currently selected program value from the dropdown (before adding).
  const [selectedUseCase, setSelectedUseCase] = useState<string | null>(null);
  const [currentProgram, setCurrentProgram] = useState<string>(DEFAULT_PROGRAM);
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(defaultErrorMessage);

  const [metadataLoading, setMetadataLoading] = useState<boolean>(false);

  const [generateDataProductSpecification] = useGenerateDataProductSpecificationMutation();
  const [getMetadataFileContent] = useLazyGetMetadataFileContentQuery();
  const [validateSnowflakeAccount] = useValidateSnowflakeAccountMutation();

  const { data: snowflakeAccounts } = useGetSnowflakeAccountsQuery({});

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (
      snowflakeAccounts !== undefined &&
      Object.keys(snowflakeAccounts).length > 0 &&
      snowflakeAccount in snowflakeAccounts
    ) {
      dispatch(setSnowflakeAccountLabel(snowflakeAccounts[snowflakeAccount]?.account));
    }
  }, [snowflakeAccounts, snowflakeAccount]);

  useEffect(() => {
    dispatch(setDataProductName(newProjectName));
  }, [newProjectName]);

  useEffect(() => {
    if (!createNewProject && selectedProject !== null) {
      // Only set the Data Product (repository) name used for branch/spec generation.
      // The human-readable "Name" field is loaded from metadata.json by loadMetadata —
      // setting it here to selectedProject.name would overwrite it with the wrong value.
      dispatch(setDataProductName(selectedProject.name));
    }
  }, [createNewProject, selectedProject]);

  useEffect(() => {
    if (!isPowerUserMode) {
      dispatch(setSkipSnowflakeConnectionStep(false));
    } else if (isPowerUserMode && snowflakeAccountConnected) {
      dispatch(setSkipSnowflakeConnectionStep(true));
    }
  }, [isPowerUserMode, snowflakeAccountConnected]);

  // Ref to store the timeout ID for debouncing
  const metadataFetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeout to implement debounce
    if (metadataFetchTimeoutRef.current !== null) {
      clearTimeout(metadataFetchTimeoutRef.current);
      metadataFetchTimeoutRef.current = null;
    }

    // Set a new timeout to fetch metadata after 1 second
    if (selectedProject !== null && selectedProject.name !== '') {
      metadataFetchTimeoutRef.current = setTimeout(() => {
        const fetchMetadata = async (): Promise<void> => {
          setMetadataLoading(true);
          const isLoadingFromMainSuccessful = await loadMetadata('main');
          if (!isLoadingFromMainSuccessful) {
            const branchName = getCamelCase(selectedProject.name);
            await loadMetadata(branchName);
          }
          setMetadataLoading(false);
        };

        // Handle the Promise without making the setTimeout callback async
        fetchMetadata().catch((err) => console.log(err));
      }, 1000);
    }

    // Cleanup function to clear the timeout when component unmounts or selectedProject changes
    return () => {
      if (metadataFetchTimeoutRef.current !== null) {
        clearTimeout(metadataFetchTimeoutRef.current);
        metadataFetchTimeoutRef.current = null;
      }
    };
  }, [selectedProject]);

  const loadMetadata = async (branchName: string): Promise<boolean> => {
    try {
      const response: any = await getMetadataFileContent(
        {
          projectPath: selectedProject.path,
          branch: branchName,
        },
        true,
      ).unwrap();

      console.log(response);

      dispatch(setName(response.metadata?.name ?? name));
      dispatch(setJiraTicketNo(response.metadata?.jira_ticket_no ?? jiraTicket));
      dispatch(setDescription(response.metadata?.description ?? description));
      dispatch(setDataProductManager(response.metadata?.data_product_manager ?? dataProductManager));
      dispatch(setVersion(response.metadata?.version ?? version));
      dispatch(setVersionDescription(response.metadata?.version_description ?? versionDescription));
      dispatch(setSchemaDescription(response.metadata?.schema_description ?? schemaDescription));
      dispatch(setDataProductCategory(response.metadata?.data_product_category ?? dataProductCategory));
      dispatch(setDataProductType(response.metadata?.data_product_type ?? dataProductType));
      dispatch(setContainsPatientData(response.metadata?.contains_patient_data ?? containsPatientData));
      dispatch(setContainsPersonalData(response.metadata?.contains_personal_data ?? containsPersonalData));
      dispatch(setIsGxpValidated(response.metadata?.is_gxp_validated ?? isGxpValidated));
      dispatch(setIsSoxControlled(response.metadata?.is_sox_controlled ?? isSoxControlled));
      dispatch(setPurpose(response.metadata?.purpose ?? purpose));
      dispatch(setSpecification1(response.metadata?.specification_1 ?? specification));
      dispatch(setSourceSystem(response.metadata?.source_system ?? sourceSystem));
      // dispatch(setApplication(response.metadata?.application ?? application));
      dispatch(setProcessingFrequency(response.metadata?.processing_frequency ?? processingFrequency));
      dispatch(setGradCode(response.metadata?.grad_code ?? gradCode));
      dispatch(setLineOfBusinessOwningDP(response.metadata?.line_of_business_owning_dp ?? lineOfBusinessOwningDP));
      dispatch(setDataDomain(response.metadata?.data_domain ?? dataDomain));

      // Task 6.2 — dispatch setRelationships when the response contains a relationships array.
      // Task 6.3 — if only legacy flat fields are present, apply frontend migration logic to
      //            reconstruct relationship pairs using the mapping table (same logic as backend).
      if (
        Array.isArray(response.metadata?.relationships) &&
        (response.metadata.relationships as unknown[]).length > 0
      ) {
        // Happy path: server already returned the structured format.
        dispatch(setRelationships(response.metadata.relationships));
      } else if (
        typeof response.metadata?.business_use_case === 'string' &&
        response.metadata.business_use_case.length > 0
      ) {
        // Migration path: legacy flat fields → reconstruct relationship pairs.
        const useCaseTokens: string[] = response.metadata.business_use_case
          .split(',')
          .map((s: string) => s.trim())
          .filter((s: string) => s.length > 0);
        const programTokens: string[] = (
          typeof response.metadata?.program === 'string' ? response.metadata.program : ''
        )
          .split(',')
          .map((s: string) => s.trim())
          .filter((s: string) => s.length > 0);

        const migratedRelationships = useCaseTokens.map((useCase: string, i: number) => {
          // Prefer the mapping table; fall back to positional program token; then DEFAULT_PROGRAM.
          const mappedProgram = USE_CASE_PROGRAM_MAPPING[useCase];
          const positionalProgram = programTokens[i];
          const program =
            mappedProgram !== undefined
              ? mappedProgram
              : positionalProgram !== undefined && positionalProgram.length > 0
              ? positionalProgram
              : DEFAULT_PROGRAM;
          return { business_use_case: useCase, program };
        });

        dispatch(setRelationships(migratedRelationships));
      } else {
        // No use-case data at all — initialise to empty.
        dispatch(setRelationships([]));
      }
      dispatch(setUrl(response.metadata?.url ?? url));
      dispatch(setBusinessTerm(response.metadata?.business_term ?? businessTerm));
      dispatch(setBusinessTermDefinition(response.metadata?.business_term_definition ?? businessTermDefinition));
      dispatch(setSecurityClassification(response.metadata?.security_classification ?? securityClassification));
      // Legacy flat program field removed — relationships array is now the source of truth (see Phase 6).

      if (response.account !== undefined) {
        try {
          await validateSnowflakeAccount({
            account: response.account,
          }).unwrap();

          dispatch(setSnowflakeAccount(String(response.account)));
          dispatch(setSnowflakeAccountConnected(true));

          if (isPowerUserMode) {
            dispatch(setSkipSnowflakeConnectionStep(true));
          }
        } catch (err) {
          console.log(err);
        }
      }

      if (response.tables !== undefined) {
        dispatch(setTablesFromStoredMetadata(response.tables));
      }

      return true; // Success
    } catch (err) {
      console.log(err);
      return false; // Failure
    }
  };

  const onContinue = (): void => {
    if (formRef.current?.reportValidity() === true) {
      if (description.length < 50) {
        setFailed(true);
        setErrorMessage('Description must be at least 50 characters.');
        return;
      }

      setLoading(true);
      setFailed(false);
      setErrorMessage(defaultErrorMessage);
      let projectName = '';
      if (createNewProject) {
        if (newProjectName === '') return;

        if (!isProjectNameValid(newProjectName)) {
          setFailed(true);
          setErrorMessage(
            'The project name is invalid. Project names must start and end with a letter or digit, avoid consecutive special characters, and include only letters, digits, underscores, dots, dashes, or spaces.',
          );
          setLoading(false);
          return;
        }

        const newProjectPath = `${selectedGroup.path}/${newProjectName}`;
        console.log('New project path:', newProjectPath);
        dispatch(setProjectPath(newProjectPath));
        projectName = newProjectPath;
        dispatch(setReviewedByInformationArchitect(false));
      } else {
        if (selectedProject.name === '') return;
        console.log('Selected project: ', selectedProject);
        dispatch(setProjectPath(selectedProject.path));
        projectName = selectedProject.path;
        dispatch(setReviewedByInformationArchitect(true));
      }

      const projectAlredyCreatedInThisSession = projectsCreatedInCurrentSession.includes(projectName);
      const isNewProject = createNewProject && !projectAlredyCreatedInThisSession;

      // For existing data products, the backend derives the branch name from `name` via
      // DataProductService.get_camel_case(name). When `newProjectName` is empty (because the
      // user selected an existing project rather than typing a new name), an empty branch
      // "refs/heads/" is produced, which Git rejects with "refusing to create funny ref".
      // Fix: pass selectedProject.name as the name for existing projects (matches the branch
      // that was created when the project was first set up) and pass the pre-computed branch
      // explicitly so the backend skips redundant branch-creation attempts.
      const specName = createNewProject ? newProjectName : selectedProject.name;
      const existingBranch = createNewProject ? undefined : getCamelCase(selectedProject.name);

      generateDataProductSpecification({
        projectName,
        isNewProject,
        name: specName,
        description,
        dataQualityTests: '',
        version,
        owner: '',
        group: selectedGroup.path,
        projectType: 'az',
        governanceData: true,
        branch: existingBranch,
      })
        .unwrap()
        .then((res: any) => {
          console.log(res);
          dispatch(setBranch(res));
          dispatch(setProjectsCreatedInCurrentSession([...projectsCreatedInCurrentSession, projectName]));
          dispatch(setModels(undefined));
          setSuccess(true);
        })
        .catch((err) => {
          console.log(err);
          if ((err.data as string).includes('Unable to create project. The project name has already been taken')) {
            setErrorMessage('Project already exists.');
          }
          setFailed(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const resetFields = (): void => {
    dispatch(setJiraTicketNo(''));
    dispatch(setDescription(''));
    dispatch(setDataProductManager(''));
    dispatch(setVersion(''));
    dispatch(setVersionDescription(''));
    dispatch(setSchemaDescription(''));
    dispatch(setDataProductCategory(''));
    dispatch(setDataProductType(''));
    dispatch(setContainsPatientData(false));
    dispatch(setContainsPersonalData(false));
    dispatch(setIsGxpValidated(false));
    dispatch(setIsSoxControlled(false));
    dispatch(setPurpose(''));
    dispatch(setSpecification1(''));
    dispatch(setSourceSystem(''));
    // dispatch(setApplication(''));
    dispatch(setProcessingFrequency(''));
    dispatch(setGradCode(''));
    dispatch(setLineOfBusinessOwningDP(''));
    dispatch(setDataDomain(''));
    dispatch(setRelationships([]));
    dispatch(setUrl(''));
    dispatch(setBusinessTerm(''));
    dispatch(setBusinessTermDefinition(''));
    dispatch(setSecurityClassification(''));
    // Legacy setProgram removed — relationships array is the source of truth.
    dispatch(clearSelectedObjects());
  };

  useEffect(() => {
    if (success) {
      props.onContinue();
    }
  }, [success]);

  // Reset Data Domain when Line of Business changes if the current value is not valid
  useEffect(() => {
    const validOptions = getDataDomainOptions(lineOfBusinessOwningDP);
    if (dataDomain !== '' && !validOptions.includes(dataDomain)) {
      dispatch(setDataDomain(''));
    }
  }, [lineOfBusinessOwningDP]);

  return (
    <StepWrapper
      title="Core Data Product attributes"
      subtitle="Fields marked with * are required"
      onBack={() => props.onBack()}
      onContinue={() => onContinue()}
      isLoading={loading}
      continueDisabled={
        loading ||
        (selectedGroup !== null && selectedGroup.name === '') ||
        (createNewProject && newProjectName === '') ||
        (!createNewProject && selectedProject !== null && selectedProject.name === '') ||
        name === '' ||
        dataProductManager === '' ||
        version === '' ||
        versionDescription === '' ||
        schemaDescription === '' ||
        dataProductCategory === '' ||
        processingFrequency === '' ||
        lineOfBusinessOwningDP === '' ||
        dataDomain === '' ||
        securityClassification === '' ||
        program === '' ||
        businessUseCase === ''
      }
    >
      <div className="h-full">
        <form ref={formRef}>
          <div className="relative mx-[auto] w-3/4 px-2 pb-2 grid grid-cols-2 gap-6">
            <div className="col-span-2">
              <DividerWithText dividerText="Data Product Set-up" />
            </div>
            <GroupAndProjectSelector
              onProjectSelectionChanged={() => resetFields()}
              onProjectSearchChanged={() => {
                // Clear any existing timeout to implement debounce when search term changes
                if (metadataFetchTimeoutRef.current !== null) {
                  clearTimeout(metadataFetchTimeoutRef.current);
                  metadataFetchTimeoutRef.current = null;
                }
              }}
            />
            {metadataLoading && (
              <div className="absolute right-4 top-5 min-h-6 flex items-center justify-center">
                <div
                  className="animate-spin inline-block w-6 h-6 border-[2px] border-current border-t-transparent text-dataops-primary rounded-full"
                  role="status"
                  aria-label="loading"
                >
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            )}

            <div className="col-span-2">
              <DividerWithText dividerText="Data Product Creation Mode" />
            </div>
            <div className="col-span-2 pb-4">
              <Field as="div" className="col-span-2 flex items-center justify-center">
                <Switch
                  checked={isPowerUserMode}
                  onChange={() => dispatch(setIsPowerUserMode(!isPowerUserMode))}
                  className="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                >
                  <span className="sr-only">Use setting</span>
                  <span aria-hidden="true" className="pointer-events-none absolute h-full w-full rounded-md bg-white" />
                  <span
                    aria-hidden="true"
                    className={classNames(
                      isPowerUserMode ? 'bg-dataops-primary-blue' : 'bg-gray-200',
                      'pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out',
                    )}
                  />
                  <span
                    aria-hidden="true"
                    className={classNames(
                      isPowerUserMode ? 'translate-x-5' : 'translate-x-0',
                      'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out',
                    )}
                  />
                </Switch>
                <Label as="span" className="ml-1 cursor-default flex">
                  <span className="text-sm font-medium text-gray-700">Create Data Product from SQL Query</span>
                  <InformationTooltip tooltip="Use this option if you have SQL queries to create a data model" />
                </Label>
              </Field>
            </div>

            <div className="col-span-2 mt-2">
              <DividerWithText dividerText="Data Product Attributes" />
            </div>
            <div className="col-span-2 xl:col-span-1">
              <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900 flex items-center">
                Name*
                <InformationTooltip tooltip="DP name to be used for Cataloguing" />
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => dispatch(setName(e.target.value))}
                    name="name"
                    id="name"
                    autoComplete="name"
                    required
                    className="block rounded-md flex-1 ring-1 ring-inset ring-gray-300 border-0 bg-transparent p-1.5 text-black placeholder:text-gray-400 sm:text-sm disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200"
                    placeholder="Customer Insight Dashboard"
                  />
                </div>
              </div>
            </div>

            <div className="col-span-2">
              <label htmlFor="description" className="block text-sm font-medium leading-6 text-gray-900">
                Description* <span className="text-gray-600">(min 50 characters)</span>
              </label>
              <div className="mt-2">
                <textarea
                  id="decription"
                  name="description"
                  value={description}
                  onChange={(e) => dispatch(setDescription(e.target.value))}
                  rows={4}
                  required
                  className="block w-full rounded-md border-0 p-1.5 text-sm text-black placeholder:text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300"
                  placeholder="This data product consolidates customer interaction data across multiple channels – including sales, customer service, and online engagement – to provide a comprehensive view of customer behaviours, preferences, and feedback. We will use this dashboard to segment our customer base, identify trends, and tailor our marketing strategies effectively and make data-driven decisions that enhance customer satisfaction and drive business growth."
                />
              </div>
            </div>

            <div className="col-span-2 xl:col-span-1">
              <label htmlFor="owner" className="block text-sm font-medium leading-6 text-gray-900">
                Data Product Manager*
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300">
                  <input
                    type="text"
                    id="owner"
                    name="owner"
                    value={dataProductManager}
                    onChange={(e) => dispatch(setDataProductManager(e.target.value))}
                    autoComplete="owner"
                    placeholder="Jane Doe"
                    required
                    className="block flex-1 border-0 bg-transparent p-1.5 text-black placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>

            <div className="col-span-2 xl:col-span-1">
              <label htmlFor="version" className="block text-sm font-medium leading-6 text-gray-900">
                Version ID*
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300">
                  <input
                    type="text"
                    id="version"
                    name="version"
                    value={version}
                    onChange={(e) => dispatch(setVersion(e.target.value))}
                    autoComplete="version"
                    placeholder="1.0.0"
                    required
                    className="block flex-1 border-0 bg-transparent p-1.5 text-black placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>

            <div className="col-span-2">
              <label htmlFor="description" className="block text-sm font-medium leading-6 text-gray-900">
                Version description*
              </label>
              <div className="mt-2">
                <textarea
                  id="decription"
                  name="description"
                  value={versionDescription}
                  onChange={(e) => dispatch(setVersionDescription(e.target.value))}
                  rows={2}
                  required
                  className="block w-full rounded-md border-0 p-1.5 text-sm text-black placeholder:text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300"
                  placeholder="Version description"
                />
              </div>
            </div>

            <div className="col-span-2">
              <label htmlFor="schema" className="block text-sm font-medium leading-6 text-gray-900">
                Schema description*
              </label>
              <div className="mt-2">
                <textarea
                  id="schema"
                  name="schema"
                  value={schemaDescription}
                  onChange={(e) => dispatch(setSchemaDescription(e.target.value))}
                  rows={2}
                  required
                  className="block w-full rounded-md border-0 p-1.5 text-sm text-black placeholder:text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300"
                  placeholder="Schema description"
                />
              </div>
            </div>

            <div className="col-span-2 xl:col-span-1">
              <DropdownSingleselect
                label="Line of Business Owning DP*"
                options={['Operations', 'R&D', 'Commercial', 'Enabling', 'Alexion']}
                selected={lineOfBusinessOwningDP}
                setSelected={(value: string) => dispatch(setLineOfBusinessOwningDP(value))}
              />
            </div>

            <div className="col-span-2 xl:col-span-1">
              <DropdownSingleselect
                label="Data Domain*"
                options={getDataDomainOptions(lineOfBusinessOwningDP)}
                selected={dataDomain}
                setSelected={(value: string) => dispatch(setDataDomain(value))}
              />
            </div>

            <div className="col-span-2 xl:col-span-1">
              <DropdownSingleselect
                label="Data Product Category*"
                options={['Foundational', 'Derived']}
                selected={dataProductCategory}
                setSelected={(value: string) => dispatch(setDataProductCategory(value))}
              />
            </div>

            <div className="col-span-2 xl:col-span-1">
              <DropdownSingleselect
                label="Processing Frequency*"
                options={['Daily', 'Weekly', 'Monthly']}
                selected={processingFrequency}
                setSelected={(value: string) => dispatch(setProcessingFrequency(value))}
              />
            </div>

            <div className="col-span-2 xl:col-span-1">
              <DropdownSingleselect
                label="Security Classification*"
                options={['Company Restricted', 'Strictly Confidential']}
                selected={securityClassification}
                setSelected={(value: string) => dispatch(setSecurityClassification(value))}
              />
            </div>

            {/* Task 2.2–2.6: Pair-builder UI — replaces the dual multi-selects */}
            <div className="col-span-2" data-testid="pair-builder">
              {/* Task 2.2 — single-select Use Case combobox (uses updated UseCaseMultiSelect with mode="single") */}
              <div className="w-full">
                <UseCaseMultiSelect
                  mode="single"
                  value={selectedUseCase ?? ''}
                  onChange={(val: string) => {
                    const newVal = val !== '' ? val : null;
                    setSelectedUseCase(newVal);
                    // Pre-fill the Program dropdown from the mapping, or fall back to DEFAULT_PROGRAM.
                    setCurrentProgram(
                      newVal !== null ? USE_CASE_PROGRAM_MAPPING[newVal] ?? DEFAULT_PROGRAM : DEFAULT_PROGRAM,
                    );
                  }}
                  excludeValues={relationships.map((r) => r.business_use_case)}
                />
              </div>

              {/* Task 2.3 — conditionally-visible Program dropdown (appears only when a use case is selected) */}
              {selectedUseCase !== null && (
                <div className="mt-4 w-full" data-testid="program-select-container">
                  <label htmlFor="program-select" className="block text-sm font-medium leading-6 text-gray-900">
                    Program
                  </label>
                  <div className="mt-2">
                    <select
                      id="program-select"
                      value={currentProgram}
                      onChange={(e) => setCurrentProgram(e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-black ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-gray-200 sm:text-sm sm:leading-6 bg-white"
                      data-testid="program-select"
                    >
                      {PROGRAM_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Task 2.4 — "Add" button: commits pair to relationships, resets state */}
              <div className="mt-4">
                <button
                  type="button"
                  disabled={selectedUseCase === null}
                  onClick={() => {
                    if (selectedUseCase === null) return;
                    const newPair = { business_use_case: selectedUseCase, program: currentProgram };
                    dispatch(setRelationships([...relationships, newPair]));
                    // Reset local state: clear the selection and hide the program field.
                    setSelectedUseCase(null);
                    setCurrentProgram(DEFAULT_PROGRAM);
                  }}
                  className={classNames(
                    'inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium shadow-sm focus:outline-none',
                    selectedUseCase !== null
                      ? 'bg-dataops-primary-blue text-white hover:bg-dataops-primary-dark cursor-pointer'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed',
                  )}
                  data-testid="add-pair-button"
                >
                  + Add Business Use Case
                </button>
              </div>

              {/* Task 2.5 — RelationshipPairList: chip-style pair display with × removal */}
              {relationships.length > 0 && (
                <div className="mt-4" data-testid="relationship-pair-list">
                  <p className="text-sm font-medium text-gray-700 mb-2">Business Use Cases list:</p>
                  <div className="flex flex-wrap gap-2">
                    {relationships.map((pair, index) => (
                      <span
                        key={`${pair.business_use_case}-${pair.program}-${index}`}
                        className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200"
                        data-testid={`pair-chip-${index}`}
                      >
                        {pair.business_use_case} — {pair.program}
                        <button
                          type="button"
                          aria-label={`Remove ${pair.business_use_case} — ${pair.program}`}
                          onClick={() => {
                            dispatch(setRelationships(relationships.filter((_, i) => i !== index)));
                          }}
                          className="ml-0.5 rounded-full text-blue-500 hover:text-blue-700 focus:outline-none"
                        >
                          <XMarkIcon className="h-3 w-3" aria-hidden="true" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="col-span-2">
              <div className="w-1/2 flex flex-col items-start">
                <Field as="div" className="flex items-center justify-center">
                  <Switch
                    checked={containsPatientData}
                    onChange={() => dispatch(setContainsPatientData(!containsPatientData))}
                    className="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                  >
                    <span className="sr-only">Use setting</span>
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute h-full w-full rounded-md bg-white"
                    />
                    <span
                      aria-hidden="true"
                      className={classNames(
                        containsPatientData ? 'bg-dataops-primary-blue' : 'bg-gray-200',
                        'pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out',
                      )}
                    />
                    <span
                      aria-hidden="true"
                      className={classNames(
                        containsPatientData ? 'translate-x-5' : 'translate-x-0',
                        'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out',
                      )}
                    />
                  </Switch>
                  <Label as="span" className="ml-1 cursor-default">
                    <span className="text-sm font-medium text-gray-700">Contains Patient Data</span>
                  </Label>
                </Field>

                <Field as="div" className="flex items-center justify-center">
                  <Switch
                    checked={containsPersonalData}
                    onChange={() => dispatch(setContainsPersonalData(!containsPersonalData))}
                    className="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                  >
                    <span className="sr-only">Use setting</span>
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute h-full w-full rounded-md bg-white"
                    />
                    <span
                      aria-hidden="true"
                      className={classNames(
                        containsPersonalData ? 'bg-dataops-primary-blue' : 'bg-gray-200',
                        'pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out',
                      )}
                    />
                    <span
                      aria-hidden="true"
                      className={classNames(
                        containsPersonalData ? 'translate-x-5' : 'translate-x-0',
                        'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out',
                      )}
                    />
                  </Switch>
                  <Label as="span" className="ml-1 cursor-default">
                    <span className="text-sm font-medium text-gray-700">Contains Personal Data</span>
                  </Label>
                </Field>

                <Field as="div" className="flex items-center justify-center">
                  <Switch
                    checked={isGxpValidated}
                    onChange={() => dispatch(setIsGxpValidated(!isGxpValidated))}
                    className="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                  >
                    <span className="sr-only">Use setting</span>
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute h-full w-full rounded-md bg-white"
                    />
                    <span
                      aria-hidden="true"
                      className={classNames(
                        isGxpValidated ? 'bg-dataops-primary-blue' : 'bg-gray-200',
                        'pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out',
                      )}
                    />
                    <span
                      aria-hidden="true"
                      className={classNames(
                        isGxpValidated ? 'translate-x-5' : 'translate-x-0',
                        'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out',
                      )}
                    />
                  </Switch>
                  <Label as="span" className="ml-1 cursor-default">
                    <span className="text-sm font-medium text-gray-700">Is GxP Validated</span>
                  </Label>
                </Field>

                <Field as="div" className="flex items-center justify-center">
                  <Switch
                    checked={isSoxControlled}
                    onChange={() => dispatch(setIsSoxControlled(!isSoxControlled))}
                    className="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                  >
                    <span className="sr-only">Use setting</span>
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute h-full w-full rounded-md bg-white"
                    />
                    <span
                      aria-hidden="true"
                      className={classNames(
                        isSoxControlled ? 'bg-dataops-primary-blue' : 'bg-gray-200',
                        'pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out',
                      )}
                    />
                    <span
                      aria-hidden="true"
                      className={classNames(
                        isSoxControlled ? 'translate-x-5' : 'translate-x-0',
                        'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out',
                      )}
                    />
                  </Switch>
                  <Label as="span" className="ml-1 cursor-default">
                    <span className="text-sm font-medium text-gray-700">Is SOx Controlled</span>
                  </Label>
                </Field>
              </div>
            </div>
          </div>
        </form>
        <div className="w-3/4 mx-[auto]">
          <LoadingAndErrorSection
            isLoading={loading}
            isFailed={failed}
            errorMessage={errorMessage}
            hideLoading={true}
          />
        </div>
      </div>
    </StepWrapper>
  );
}
